import { useQuery } from "@tanstack/react-query";

import { useAccessContext } from "@/hooks/useAccessContext";
import { useOrganization } from "@/hooks/useOrganization";
import { supabase } from "@/integrations/supabase/client";

const SCHOOL_HEALTH_ROLES = [
  "admin",
  "super_admin",
  "platform_super_admin",
  "staff",
  "youth",
  "ministry_leader",
];

const SCHOOL_ATTENDANCE_ROLES = [
  "admin",
  "pastor",
  "staff",
  "ministry_leader",
  "youth",
  "super_admin",
  "platform_super_admin",
];

const SCHOOL_FINANCE_ROLES = [
  "finance_admin",
  "bookkeeper",
  "finance",
  "admin",
  "super_admin",
  "platform_super_admin",
];

const SCHOOL_AID_ROLES = [
  "finance_admin",
  "finance",
  "admin",
  "super_admin",
  "platform_super_admin",
];

function hasAnyRole(roles: readonly string[] | null | undefined, allowed: readonly string[]) {
  return allowed.some((role) => roles?.includes(role));
}

function useRoleAccess(allowedRoles: readonly string[]) {
  const { data: accessContext, isLoading } = useAccessContext();
  const roles = accessContext?.org_app_roles ?? [];
  return {
    allowed: Boolean(accessContext?.capabilities.isOrgAdmin) || hasAnyRole(roles, allowedRoles),
    isLoading,
  };
}

export function useFinanceAccess(): { allowed: boolean; isLoading: boolean } {
  const { data: accessContext, isLoading } = useAccessContext();
  return {
    allowed: Boolean(accessContext?.capabilities.canAccessModule("contributions")),
    isLoading,
  };
}

export function useFeeScheduleFinanceAccess(): {
  allowed: boolean;
  isLoading: boolean;
} {
  return useRoleAccess(SCHOOL_FINANCE_ROLES);
}

export function useSchoolFinanceOperationsAccess(): {
  allowed: boolean;
  isLoading: boolean;
} {
  return useRoleAccess(SCHOOL_FINANCE_ROLES);
}

export function useSchoolAidManagementAccess(): {
  allowed: boolean;
  isLoading: boolean;
} {
  return useRoleAccess(SCHOOL_AID_ROLES);
}

export function useSchoolAcademicsAccess(): {
  allowed: boolean;
  isLoading: boolean;
} {
  const { organizationId } = useOrganization();
  const { data: accessContext, isLoading: accessLoading } = useAccessContext();
  const query = useQuery({
    queryKey: ["schools", "academics", "access", organizationId],
    queryFn: async () => {
      if (!organizationId) return false;
      const { data, error } = await supabase.rpc("school_can_manage_academics" as never, {
        _org_id: organizationId,
      } as never);
      if (error) throw error;
      return data === true;
    },
    enabled: Boolean(organizationId) && !accessLoading,
    staleTime: 30_000,
  });

  return {
    allowed: Boolean(accessContext?.capabilities.isOrgAdmin) || query.data === true,
    isLoading: accessLoading || query.isLoading,
  };
}

export function useSchoolHealthAccess(): {
  allowed: boolean;
  isLoading: boolean;
} {
  return useRoleAccess(SCHOOL_HEALTH_ROLES);
}

export function useSchoolAttendanceAccess(): {
  allowed: boolean;
  isLoading: boolean;
} {
  return useRoleAccess(SCHOOL_ATTENDANCE_ROLES);
}
